import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { legacySiteId, type LegacySiteId } from './LegacySite.js'

/** Identifier type for public.sensor_site_label_ */
export type SensorSiteLabelSensorSn = string & {
  __brand: 'SensorSiteLabelSensorSn'
}

/**
 * Represents the table public.sensor_site_label_
 * Chart line name a project admin typed for one sensor on one site (borehole dashboard rename). Shown only on that site, so it does not follow a logger to another customer's site. The Orion automatic label stays in sensor_display_ and follows the device.
 */
export default interface SensorSiteLabelTable {
  /** Database type: pg_catalog.int4 */
  siteId: ColumnType<LegacySiteId, LegacySiteId, LegacySiteId>

  /** Database type: pg_catalog.text */
  sensorSn: ColumnType<
    SensorSiteLabelSensorSn,
    SensorSiteLabelSensorSn,
    SensorSiteLabelSensorSn
  >

  /** Database type: pg_catalog.text */
  loggerSn: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  label: ColumnType<string, string, string>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date, Date | string | undefined, Date | string>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date, Date | string | undefined, Date | string>
}

export type SensorSiteLabel = Selectable<SensorSiteLabelTable>

export type SensorSiteLabelInitializer = Insertable<SensorSiteLabelTable>

export type SensorSiteLabelMutator = Updateable<SensorSiteLabelTable>

export const sensorSiteLabelSensorSn =
  z.string() as unknown as z.Schema<SensorSiteLabelSensorSn>

export const sensorSiteLabel = z.object({
  siteId: legacySiteId,
  sensorSn: sensorSiteLabelSensorSn,
  loggerSn: z.string(),
  label: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
}) as unknown as z.Schema<SensorSiteLabel>

export const sensorSiteLabelInitializer = z.object({
  siteId: legacySiteId,
  sensorSn: sensorSiteLabelSensorSn,
  loggerSn: z.string(),
  label: z.string(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}) as unknown as z.Schema<SensorSiteLabelInitializer>

export const sensorSiteLabelMutator = z.object({
  siteId: legacySiteId.optional(),
  sensorSn: sensorSiteLabelSensorSn.optional(),
  loggerSn: z.string().optional(),
  label: z.string().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}) as unknown as z.Schema<SensorSiteLabelMutator>
